import { motion } from "framer-motion";
import { CalendarDays, MapPin, ArrowRight, Ticket } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Countdown } from "@/components/Countdown";
import { useFormDialogs } from "@/components/FormDialogs";
import { getRegistrationUrl, EVENT_ENDED } from "@/config";
import { AddToCalendar } from "@/components/AddToCalendar";

const HERO_BG = "/img/site-bg.jpg";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const h = t.hero;
  const { openPartner } = useFormDialogs();

  return (
    <section id="hero" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="absolute inset-0 bg-[#01154F]/85" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#01154F] via-[#01154F]/40 to-transparent" />
      <div className="absolute inset-0 grain opacity-[0.04] pointer-events-none" />

      <div className="container-page relative z-10 pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="flex w-fit mx-auto items-center gap-2 mb-8">
            <span className="h-1.5 w-1.5 bg-[#CEA26B]" />
            <span className="overline text-slate-200">{h.badge}</span>
          </div>

          {EVENT_ENDED && (
            <div
              className="mb-8 inline-flex items-center gap-3 border-2 border-[#CEA26B]/60 bg-[#CEA26B]/10 px-6 py-3 sm:px-8 sm:py-4 font-display font-bold tracking-tight text-[#CEA26B] text-2xl sm:text-3xl lg:text-4xl"
              data-testid="hero-thanks-banner"
            >
              <span className="h-2.5 w-2.5 sm:h-3 sm:w-3 bg-[#CEA26B]" />
              {h.thanksBanner}
            </div>
          )}

          <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {h.title}
          </h1>
          <p className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl text-[#CEA26B] font-light tracking-tight">
            {EVENT_ENDED ? h.taglinePast : h.tagline}
          </p>
          {h.yearTheme && (
            <p
              className="mt-5 inline-flex items-center gap-2.5 border border-[#CEA26B]/50 bg-[#CEA26B]/10 px-5 py-2 text-sm sm:text-base font-semibold tracking-wide text-white"
              data-testid="hero-year-theme"
            >
              <span className="h-1.5 w-1.5 bg-[#CEA26B]" />
              {h.yearTheme}
            </p>
          )}

          <div className="mt-7 space-y-2 max-w-2xl mx-auto">
            {(EVENT_ENDED ? h.introLinesPast : h.introLines).map((line, i) => (
              <p key={i} className="text-base md:text-lg text-slate-300 leading-relaxed">{line}</p>
            ))}
          </div>

          {EVENT_ENDED && (
            <div className="mt-10 flex flex-col items-center gap-5" data-testid="hero-next-edition">
              <div className="inline-flex items-center gap-3 bg-[#CEA26B] px-7 py-4 text-[#01154F] shadow-lg shadow-[#CEA26B]/20">
                <CalendarDays className="h-6 w-6" />
                <div className="text-left">
                  <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-extrabold text-[#01154F]">{h.nextEditionLabel}</div>
                  <div className="font-display font-black tracking-tight text-lg sm:text-xl text-[#01154F]">{h.nextEditionValue}</div>
                </div>
              </div>
              <AddToCalendar />
            </div>
          )}

          {!EVENT_ENDED && (
          <>
          <div className="mt-12 flex flex-wrap gap-x-12 gap-y-6 border-t border-white/10 pt-8 justify-center">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-[#CEA26B]" />
              <div className="text-left">
                <div className="overline text-slate-500">{h.dateLabel}</div>
                <div className="text-white font-medium">{h.dateValue}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[#CEA26B]" />
              <div className="text-left">
                <div className="overline text-slate-500">{h.locationLabel}</div>
                <div className="text-white font-medium">{h.locationValue}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <AddToCalendar />
          </div>

          <p className="mt-8 inline-flex items-center gap-2.5 text-sm md:text-base text-slate-200">
            <Ticket className="h-4 w-4 text-[#CEA26B]" />
            {h.ticketLine}
          </p>
          </>
          )}

          <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
            {!EVENT_ENDED && (
            <a
              href={getRegistrationUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-register-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-8 py-4 font-semibold tracking-wide transition-colors"
            >
              {h.ctaTicket}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
            )}
            <button
              onClick={() => openPartner()}
              data-testid="hero-partner-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:bg-white/10 text-white px-8 py-4 font-semibold tracking-wide transition-colors"
            >
              {h.ctaPartner}
            </button>
          </div>

          {!EVENT_ENDED && (
          <div className="mt-12">
            <Countdown />
          </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
